/**
 * FikoDepthEstimator - Monoküler Derinlik Tahmini
 *
 * Tek görüntüden derinlik haritası oluşturma.
 * Basit gradient tabanlı tahmin (gerçek uygulamada ML model kullanılmalı).
 */

/**
 * Depth Estimator sınıfı
 */
export class FikoDepthEstimator {
    private width: number = 0;
    private height: number = 0;
    private lastDepthMap: Float32Array | null = null;

    constructor() {}

    /**
     * Derinlik tahmini yap
     *
     * Birden fazla görsel ipucu kullanarak derinlik tahmini:
     * 1. Gradient magnitude - kenarlar/detaylar
     * 2. Parlaklık - koyu alanlar genellikle gölgeli/uzak
     * 3. Texture yoğunluğu - detaylı alanlar yakın
     */
    estimate(image: ImageData): Float32Array {
        this.width = image.width;
        this.height = image.height;

        const depthMap = new Float32Array(this.width * this.height);

        // Grayscale'e çevir
        const gray = this.toGrayscale(image);

        // 1. Gradient magnitude (kenar/detay algılama)
        const gradientX = this.sobelX(gray);
        const gradientY = this.sobelY(gray);
        const gradientMag = new Float32Array(this.width * this.height);
        for (let i = 0; i < gradientMag.length; i++) {
            gradientMag[i] = Math.sqrt(gradientX[i] ** 2 + gradientY[i] ** 2);
        }

        // 2. Local texture variance (küçük pencerede varyans)
        const textureVar = this.computeLocalVariance(gray, 5);

        // Blur uygula
        const blurredGrad = this.gaussianBlur(gradientMag, 5);
        const blurredVar = this.gaussianBlur(textureVar, 5);

        // Min/max normalizasyonu
        let maxGrad = -Infinity, minGrad = Infinity;
        let maxVar = -Infinity, minVar = Infinity;
        let maxGray = -Infinity, minGray = Infinity;

        for (let i = 0; i < this.width * this.height; i++) {
            if (blurredGrad[i] > maxGrad) maxGrad = blurredGrad[i];
            if (blurredGrad[i] < minGrad) minGrad = blurredGrad[i];
            if (blurredVar[i] > maxVar) maxVar = blurredVar[i];
            if (blurredVar[i] < minVar) minVar = blurredVar[i];
            if (gray[i] > maxGray) maxGray = gray[i];
            if (gray[i] < minGray) minGray = gray[i];
        }

        const gradRange = maxGrad - minGrad || 1;
        const varRange = maxVar - minVar || 1;
        const grayRange = maxGray - minGray || 1;

        // Derinlik hesapla - birden fazla ipucu birleştir
        for (let i = 0; i < depthMap.length; i++) {
            // Normalize edilmiş değerler (0-1)
            const normGrad = (blurredGrad[i] - minGrad) / gradRange;
            const normVar = (blurredVar[i] - minVar) / varRange;
            const normGray = (gray[i] - minGray) / grayRange;

            // Derinlik ipuçları:
            // - Yüksek gradient = kenar/obje = yakın (ağırlık: 0.5)
            // - Yüksek texture varyansı = detay = yakın (ağırlık: 0.3)
            // - Orta parlaklık = normal yüzey, aşırı koyu/açık = özel durum (ağırlık: 0.2)
            const gradDepth = normGrad;                              // 0=uzak, 1=yakın
            const varDepth = normVar;                                // 0=uzak, 1=yakın
            const grayDepth = 1 - Math.abs(normGray - 0.5) * 2;     // 0.5'e yakın = normal

            // Ağırlıklı kombinasyon
            const combinedDepth = gradDepth * 0.5 + varDepth * 0.3 + grayDepth * 0.2;

            // Derinlik aralığı: 200mm - 1000mm (yüzey varyasyonu için)
            // Yüksek combined = yakın = düşük depth değeri
            depthMap[i] = 1000 - combinedDepth * 800;
        }

        // Temporal smoothing (önceki frame ile karıştır)
        if (this.lastDepthMap && this.lastDepthMap.length === depthMap.length) {
            const alpha = 0.6; // Yeni frame ağırlığı
            for (let i = 0; i < depthMap.length; i++) {
                depthMap[i] = alpha * depthMap[i] + (1 - alpha) * this.lastDepthMap[i];
            }
        }

        this.lastDepthMap = depthMap.slice();
        return depthMap;
    }

    /**
     * Lokal varyans hesapla (texture yoğunluğu için)
     */
    private computeLocalVariance(gray: Float32Array, windowSize: number): Float32Array {
        const result = new Float32Array(this.width * this.height);
        const half = Math.floor(windowSize / 2);

        for (let y = half; y < this.height - half; y++) {
            for (let x = half; x < this.width - half; x++) {
                let sum = 0;
                let sumSq = 0;
                let count = 0;

                for (let wy = -half; wy <= half; wy++) {
                    for (let wx = -half; wx <= half; wx++) {
                        const val = gray[(y + wy) * this.width + (x + wx)];
                        sum += val;
                        sumSq += val * val;
                        count++;
                    }
                }

                const mean = sum / count;
                const variance = (sumSq / count) - (mean * mean);
                result[y * this.width + x] = Math.sqrt(Math.max(0, variance));
            }
        }

        return result;
    }

    /**
     * Grayscale'e çevir
     */
    private toGrayscale(image: ImageData): Float32Array {
        const gray = new Float32Array(image.width * image.height);

        for (let i = 0; i < gray.length; i++) {
            const idx = i * 4;
            gray[i] = (
                0.299 * image.data[idx] +
                0.587 * image.data[idx + 1] +
                0.114 * image.data[idx + 2]
            ) / 255;
        }

        return gray;
    }

    /**
     * Sobel X (yatay gradient)
     */
    private sobelX(gray: Float32Array): Float32Array {
        const result = new Float32Array(this.width * this.height);

        const kernel = [
            [-1, 0, 1],
            [-2, 0, 2],
            [-1, 0, 1]
        ];

        for (let y = 1; y < this.height - 1; y++) {
            for (let x = 1; x < this.width - 1; x++) {
                let sum = 0;

                for (let ky = -1; ky <= 1; ky++) {
                    for (let kx = -1; kx <= 1; kx++) {
                        const idx = (y + ky) * this.width + (x + kx);
                        sum += gray[idx] * kernel[ky + 1][kx + 1];
                    }
                }

                result[y * this.width + x] = sum;
            }
        }

        return result;
    }

    /**
     * Sobel Y (dikey gradient)
     */
    private sobelY(gray: Float32Array): Float32Array {
        const result = new Float32Array(this.width * this.height);

        const kernel = [
            [-1, -2, -1],
            [0, 0, 0],
            [1, 2, 1]
        ];

        for (let y = 1; y < this.height - 1; y++) {
            for (let x = 1; x < this.width - 1; x++) {
                let sum = 0;

                for (let ky = -1; ky <= 1; ky++) {
                    for (let kx = -1; kx <= 1; kx++) {
                        const idx = (y + ky) * this.width + (x + kx);
                        sum += gray[idx] * kernel[ky + 1][kx + 1];
                    }
                }

                result[y * this.width + x] = sum;
            }
        }

        return result;
    }

    /**
     * Gaussian blur
     */
    private gaussianBlur(input: Float32Array, kernelSize: number): Float32Array {
        const result = new Float32Array(input.length);
        const halfKernel = Math.floor(kernelSize / 2);

        // Gaussian kernel oluştur
        const kernel = this.createGaussianKernel(kernelSize, kernelSize / 3);

        for (let y = halfKernel; y < this.height - halfKernel; y++) {
            for (let x = halfKernel; x < this.width - halfKernel; x++) {
                let sum = 0;
                let weightSum = 0;

                for (let ky = -halfKernel; ky <= halfKernel; ky++) {
                    for (let kx = -halfKernel; kx <= halfKernel; kx++) {
                        const idx = (y + ky) * this.width + (x + kx);
                        const weight = kernel[(ky + halfKernel) * kernelSize + (kx + halfKernel)];
                        sum += input[idx] * weight;
                        weightSum += weight;
                    }
                }

                result[y * this.width + x] = sum / weightSum;
            }
        }

        return result;
    }

    /**
     * Gaussian kernel oluştur
     */
    private createGaussianKernel(size: number, sigma: number): Float32Array {
        const kernel = new Float32Array(size * size);
        const center = Math.floor(size / 2);
        let sum = 0;

        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                const dx = x - center;
                const dy = y - center;
                const value = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));
                kernel[y * size + x] = value;
                sum += value;
            }
        }

        // Normalize
        for (let i = 0; i < kernel.length; i++) {
            kernel[i] /= sum;
        }

        return kernel;
    }

    /**
     * Derinlik haritasını görselleştir (debug için)
     */
    visualize(depthMap: Float32Array, width: number, height: number): ImageData {
        const imageData = new ImageData(width, height);

        // NOT: Math.max(...array) büyük dizilerde stack overflow yapar
        let min = Infinity;
        let max = -Infinity;
        for (let i = 0; i < depthMap.length; i++) {
            if (depthMap[i] < min) min = depthMap[i];
            if (depthMap[i] > max) max = depthMap[i];
        }
        const range = max - min || 1;

        for (let i = 0; i < depthMap.length; i++) {
            const normalized = (depthMap[i] - min) / range;
            const value = Math.floor(normalized * 255);

            const idx = i * 4;
            imageData.data[idx] = value;     // R
            imageData.data[idx + 1] = value; // G
            imageData.data[idx + 2] = value; // B
            imageData.data[idx + 3] = 255;   // A
        }

        return imageData;
    }

    /**
     * Temizle
     */
    dispose(): void {
        this.lastDepthMap = null;
    }
}
